// Trykkeguide (/trykkeguide), i startsidens stil (planer/ny-kundesamhandling-plan.md): klubbene med
// riktig plassering av trykk på plaggene. Søk, butikk og gren, «Ny klubb», og QR-kode til mobilen.
// Hver klubb åpnes på /trykkeguide/:id (TrykkeguideKlubb.jsx).
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { GRENER, STORE_LABELS, capitalize } from "../constants/trykkeguide";
import { Tilbake, Sidetopp, Tekstfelt, Valgfelt, Valgknapper, feltKlasse, knappPrimar, knappSekundar } from "../ny-kundesamhandling/Skjema";
import Ikon from "../ny-kundesamhandling/Ikon";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const BUTIKKVALG = [
  { verdi: "", tittel: "Alle" },
  { verdi: "ROYKEN", tittel: "Røyken" },
  { verdi: "SLEMMESTAD", tittel: "Slemmestad" },
];

function StoreBadge({ store }) {
  const cls = store === "ROYKEN" ? "bg-indigo-100 text-indigo-900" : "bg-teal-100 text-teal-900";
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-sm font-semibold ${cls}`}>{STORE_LABELS[store] || store}</span>;
}

// Grener som av/på-knapper (aria-pressed).
function GrenPicker({ id, selected, onChange }) {
  const toggle = (gren) => onChange(selected.includes(gren) ? selected.filter((g) => g !== gren) : [...selected, gren]);
  return (
    <div role="group" aria-labelledby={id} className="space-y-1.5">
      <span id={id} className="block font-semibold text-slate-800">Grener (valgfritt)</span>
      <div className="flex flex-wrap gap-2">
        {GRENER.map((gren) => (
          <button
            key={gren}
            type="button"
            aria-pressed={selected.includes(gren)}
            onClick={() => toggle(gren)}
            className={`min-h-[2.25rem] rounded-xl border-2 px-3 py-1.5 font-semibold transition touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${
              selected.includes(gren) ? "border-violet-700 bg-violet-700 text-white" : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
            }`}
          >
            {capitalize(gren)}
          </button>
        ))}
      </div>
    </div>
  );
}

function NewClubForm({ onCreated, onCancel }) {
  const [name, setName] = useState("");
  const [butikktilhorighet, setButikktilhorighet] = useState("");
  const [link, setLink] = useState("");
  const [grener, setGrener] = useState([]);
  const [saving, setSaving] = useState(false);
  const [feil, setFeil] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setFeil("Skriv inn klubbnavnet."); return; }
    if (!butikktilhorighet) { setFeil("Velg hvilken butikk klubben hører til."); return; }
    setSaving(true);
    setFeil("");
    try {
      const r = await fetch(`${API_BASE_URL}/trykkeguide`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, butikktilhorighet, link, grener }),
      });
      if (!r.ok) throw new Error();
      onCreated(await r.json());
    } catch {
      setFeil("Klarte ikke å opprette klubben. Prøv igjen.");
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} aria-labelledby="tg-ny-tittel" className="space-y-3 rounded-2xl border-2 border-violet-200 bg-violet-50 p-3 sm:p-3">
      <h2 id="tg-ny-tittel" className="text-base font-bold text-slate-900">Ny klubb</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <Tekstfelt id="tg-ny-navn" etikett="Klubbnavn" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        <Valgfelt id="tg-ny-butikk" etikett="Butikk" value={butikktilhorighet} onChange={(e) => setButikktilhorighet(e.target.value)} required>
          <option value="">Velg butikk</option>
          <option value="ROYKEN">Røyken</option>
          <option value="SLEMMESTAD">Slemmestad</option>
        </Valgfelt>
      </div>
      <Tekstfelt id="tg-ny-lenke" etikett="Lenke til nettbutikk (valgfritt)" type="url" inputMode="url" value={link} onChange={(e) => setLink(e.target.value)} />
      <GrenPicker id="tg-ny-grener" selected={grener} onChange={setGrener} />
      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={saving} className={knappPrimar}>
          <Ikon navn="hake" className="w-6 h-6" /> {saving ? "Lagrer …" : "Opprett klubb"}
        </button>
        <button type="button" onClick={onCancel} className={knappSekundar}>Avbryt</button>
      </div>
    </form>
  );
}

function ClubCard({ club }) {
  return (
    <li className="flex min-w-0 flex-col rounded-2xl border-2 border-slate-200 bg-white transition hover:border-violet-300">
      <Link
        to={`/trykkeguide/${club._id}`}
        className="group flex flex-1 items-center gap-2 rounded-2xl p-3 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
      >
        {club.logo?.url ? (
          <img src={club.logo.url} alt="" className="h-11 w-11 shrink-0 rounded-lg border border-slate-100 object-contain" />
        ) : (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-800">
            <Ikon navn="genser" className="w-7 h-7" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block font-bold text-slate-900 [overflow-wrap:anywhere]">{club.name}</span>
          <span className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-700">
            <StoreBadge store={club.butikktilhorighet} />
            {club.garments?.length || 0} plagg i guiden
          </span>
        </span>
        <Ikon navn="pil" className="w-5 h-5 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-indigo-700" />
      </Link>
      {club.link && (
        <a href={club.link} target="_blank" rel="noreferrer"
          className="border-t border-slate-200 px-3 py-1.5 text-sm font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900">
          Nettbutikken til {club.name}<span className="sr-only"> (åpnes i ny fane)</span>
        </a>
      )}
    </li>
  );
}

function Trykkeguide() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feil, setFeil] = useState("");
  const [search, setSearch] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [grenFilter, setGrenFilter] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);

  useEffect(() => {
    async function fetchClubs() {
      try {
        const r = await fetch(`${API_BASE_URL}/trykkeguide`);
        if (!r.ok) throw new Error();
        setClubs(await r.json());
      } catch {
        setFeil("Klarte ikke å hente klubbene. Last siden på nytt om litt.");
      } finally {
        setLoading(false);
      }
    }
    fetchClubs();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clubs.filter((c) =>
      (!q || c.name.toLowerCase().includes(q)) &&
      (!storeFilter || c.butikktilhorighet === storeFilter) &&
      (!grenFilter || (c.grener || []).includes(grenFilter)));
  }, [clubs, search, storeFilter, grenFilter]);

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />

      <Sidetopp ikon="bok" farge="trykk" tittel="Trykkeguide" undertittel="Velg klubb for riktig plassering av trykk på plaggene.">
        <div className="flex flex-wrap items-center gap-2">
          <figure className="flex flex-col items-center rounded-xl border border-slate-200 bg-white p-2">
            <QRCodeSVG value={`${window.location.origin}/trykkeguide`} size={64} level="M" title="QR-kode til trykkeguiden" />
            <figcaption className="mt-1 text-xs text-slate-600">Skann for mobil</figcaption>
          </figure>
          {!showNewForm && (
            <button type="button" onClick={() => setShowNewForm(true)} className={knappSekundar}>
              <Ikon navn="pluss" className="w-5 h-5" /> Ny klubb
            </button>
          )}
        </div>
      </Sidetopp>

      {showNewForm && (
        <NewClubForm
          onCreated={(club) => { setClubs((prev) => [...prev, club].sort((a, b) => a.name.localeCompare(b.name, "nb"))); setShowNewForm(false); }}
          onCancel={() => setShowNewForm(false)}
        />
      )}

      <section aria-label="Søk og utvalg" className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div>
          <label htmlFor="tg-sok" className="mb-1 block font-semibold text-slate-800">Søk etter klubb</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"><Ikon navn="sok" className="w-6 h-6" /></span>
            <input id="tg-sok" type="search" inputMode="search" autoComplete="off" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Klubbnavn" aria-describedby="tg-sok-status" className={`${feltKlasse} pl-12`} />
          </div>
          <p id="tg-sok-status" role="status" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
            {!loading && (search.trim() || storeFilter || grenFilter) ? `${filtered.length} ${filtered.length === 1 ? "klubb" : "klubber"} passer` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <Valgknapper id="tg-butikk" etikett="Butikk" valg={BUTIKKVALG} verdi={storeFilter} onVelg={setStoreFilter} />
          <Valgfelt id="tg-gren" etikett="Gren" value={grenFilter} onChange={(e) => setGrenFilter(e.target.value)} className="w-56 max-w-full">
            <option value="">Alle grener</option>
            {GRENER.map((gren) => <option key={gren} value={gren}>{capitalize(gren)}</option>)}
          </Valgfelt>
        </div>
      </section>

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      {loading ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter klubber …</p>
      ) : filtered.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Ingen klubber passer.</p>
      ) : (
        <ul aria-label="Klubber" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((club) => <ClubCard key={club._id} club={club} />)}
        </ul>
      )}
    </div>
  );
}

export default Trykkeguide;
